import axios from 'axios';
import { datalogger } from '../../../../../utils/index.jsx';
import { shouldServeMock } from '../demoMock';

const ANALYTICS_BASE_URL = `${import.meta.env.VITE_APP_API_URL}/analytics`;

const MOCK_CONFIG = {
  enabled: import.meta.env.VITE_MOCK_SURGERY === 'true',
  endpoints: {
    'quick-kpis': true,
    'by-procedure': true,
    'by-surgeon': true,
    'waiting-time-distribution': true,
    'pipeline-by-status': true,
  },
  delay: 300,
};

const EMPTY_FILTERS = {
  serviceInvestigationIds: [],
  procedureServiceIds: [],
  surgeonResearcherUuids: [],
  statuses: [],
};

export function buildSurgeryAnalyticsQuery(filters = EMPTY_FILTERS) {
  const params = new URLSearchParams();
  if (filters.serviceInvestigationIds?.length) {
    params.set('service', filters.serviceInvestigationIds.join(','));
  }
  if (filters.procedureServiceIds?.length) {
    params.set('procedure', filters.procedureServiceIds.join(','));
  }
  if (filters.surgeonResearcherUuids?.length) {
    params.set('surgeon', filters.surgeonResearcherUuids.join(','));
  }
  if (filters.statuses?.length) {
    params.set('status', filters.statuses.join(','));
  }
  const query = params.toString();
  return query ? `?${query}` : '';
}

const buildSurgeryUrl = (endpointPath, { uuidInvestigation, uuidDepartment, startDate, endDate, filters }) => {
  const base = `${ANALYTICS_BASE_URL}/${uuidInvestigation}/surgery/${endpointPath}/${uuidDepartment}/startDate/${startDate}/endDate/${endDate}`;
  return `${base}${buildSurgeryAnalyticsQuery(filters)}`;
};

const getMockQuickKpis = () => ({
  status: 200,
  stats: {
    totals: {
      planned: 12,
      completed: 9,
      cancelled: 2,
      currentlyWaitingPatients: 5,
      medianReadyToOperationDays: 8.5,
    },
  },
});

const getMockByProcedure = () => ({
  status: 200,
  stats: {
    rows: [
      {
        procedureKey: 'arth',
        procedureLabel: 'Arthrodèse',
        episodeCount: 8,
        completed: 6,
        cancelled: 1,
        medianReadyToOperationDays: 7,
      },
      {
        procedureKey: 'men',
        procedureLabel: 'Méniscectomie',
        episodeCount: 5,
        completed: 4,
        cancelled: 0,
        medianReadyToOperationDays: 5,
      },
    ],
  },
});

const getMockBySurgeon = () => ({
  status: 200,
  stats: {
    rows: [
      {
        surgeonUuid: 'surgeon-1',
        surgeonLabel: 'Dr Smith',
        episodeCount: 6,
        completed: 5,
        planned: 1,
      },
    ],
  },
});

const getMockWaitingTime = () => ({
  status: 200,
  stats: {
    buckets: [
      { label: '0-7', minDays: 0, maxDays: 7, count: 4 },
      { label: '8-14', minDays: 8, maxDays: 14, count: 3 },
      { label: '15-30', minDays: 15, maxDays: 30, count: 2 },
      { label: '31+', minDays: 31, maxDays: null, count: 1 },
    ],
  },
});

const getMockPipeline = () => ({
  status: 200,
  stats: {
    rows: [
      { status: 16, count: 5 },
      { status: 7, count: 9 },
      { status: 5, count: 2 },
    ],
  },
});

const MOCK_REGISTRY = {
  'quick-kpis': getMockQuickKpis,
  'by-procedure': getMockByProcedure,
  'by-surgeon': getMockBySurgeon,
  'waiting-time-distribution': getMockWaitingTime,
  'pipeline-by-status': getMockPipeline,
};

const getAuthHeaders = () => ({
  headers: { Authorization: localStorage.getItem('jwt') },
});

const normalizeFilters = (filters) => ({
  serviceInvestigationIds: filters?.serviceInvestigationIds ?? [],
  procedureServiceIds: filters?.procedureServiceIds ?? [],
  surgeonResearcherUuids: filters?.surgeonResearcherUuids ?? [],
  statuses: filters?.statuses ?? [],
});

const makeSurgeryRequest = async (params, signal) => {
  const { endpointPath, filters, ...rest } = params;
  const url = buildSurgeryUrl(endpointPath, { ...rest, filters: normalizeFilters(filters) });

  if (shouldServeMock(endpointPath, MOCK_CONFIG, MOCK_REGISTRY)) {
    const mockFn = MOCK_REGISTRY[endpointPath];
    if (mockFn) {
      await new Promise((resolve) => setTimeout(resolve, MOCK_CONFIG.delay));
      return mockFn();
    }
  }

  const response = await axios.get(url, { ...getAuthHeaders(), signal });
  if (response.status === 200) {
    return response.data;
  }
  throw response.data;
};

const createEndpoint = (endpointPath) =>
  datalogger((uuidInvestigation, uuidDepartment, startDate, endDate, filters, signal) =>
    makeSurgeryRequest(
      {
        endpointPath,
        uuidInvestigation,
        uuidDepartment,
        startDate,
        endDate,
        filters,
      },
      signal,
    ),
  );

export const surgeryServices = {
  getQuickKpis: createEndpoint('quick-kpis'),
  getByProcedure: createEndpoint('by-procedure'),
  getBySurgeon: createEndpoint('by-surgeon'),
  getWaitingTimeDistribution: createEndpoint('waiting-time-distribution'),
  getPipelineByStatus: createEndpoint('pipeline-by-status'),
};

export { buildSurgeryAnalyticsQuery as buildSurgeryAnalyticsQueryForTest };
